"use client";

import { Button } from "@/components/ui/button";
import { useSession } from "@/lib/auth-client";
import { useTranslation } from "@/lib/i18n";
import { Plan } from "@/lib/shared/payment/types";
import { cn } from "@/lib/utils";
import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import GradientText from "./GradientText";
type PricingPlanSectionProps = {
  plans: Plan[];
};

const LngToCurrencyMap: Record<string, string> = {
  en: "usd",
  zh: "cny",
};

const CurrencySymbolMap: Record<string, string> = {
  usd: "$",
  cny: "¥",
};

const PricingPlanSection: React.FC<PricingPlanSectionProps> = (props) => {
  const { plans } = props;

  const { t, i18n } = useTranslation({ ns: "pricing" });

  const [loading, setLoading] = useState(false);

  const { data: session } = useSession();

  const { currency, currencySymbol } = useMemo(() => {
    const lng = i18n.language;
    const currency = LngToCurrencyMap[lng] || "usd";
    const currencySymbol = CurrencySymbolMap[currency] || "$";
    return { currency, currencySymbol };
  }, [i18n.language]);

  const plusPlan = useMemo(() => {
    return plans.find((plan: any) => plan.id === "plus");
  }, [plans]);

  const plusPlanPrices = useMemo(() => {
    return plusPlan?.prices?.sort((a, b) => {
      return Number(a.metadata?.order) - Number(b.metadata?.order);
    });
  }, [plusPlan]);

  const getCheckoutSession = async (priceId: string) => {
    const paymentMode = i18n.language === "zh" ? "one_time" : "recurring";
    setLoading(true);
    try {
      const response = await fetch(`/api/payment/checkout-session`, {
        method: "POST",
        body: JSON.stringify({
          priceId,
          quantity: 1,
          paymentMode,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        window.location.href = data;
      } else {
        toast.error("Please try again later");
      }
    } catch (error) {
      toast.error("Please try again later");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative mt-14 flex flex-col items-center gap-8">
      <div className="grid grid-cols-3 items-center gap-8">
        {plusPlanPrices &&
          plusPlanPrices.map((price) => {
            const isFeatured = price.metadata?.featured === "true";
            const interval =
              price.metadata?.custom_interval || price.recurring?.interval;
            const intervalCount =
              price.metadata?.custom_interval_count ||
              price.recurring?.interval_count;

            return (
              <div
                key={price.id}
                className={cn(
                  "flex h-fit flex-col gap-8 rounded-2xl border p-6",
                  isFeatured && "p-7 text-white",
                )}
                style={{
                  background: isFeatured
                    ? "linear-gradient(101deg, #0A4145 -3.56%, #3E986C 36.5%, #A7B542 70.07%, #E1A602 104.71%)"
                    : "white",
                  borderColor: isFeatured ? "#E8F2EA" : "#7DB98C",
                  boxShadow: isFeatured
                    ? "0px 3.841px 38.411px 0px rgba(4, 39, 18, 0.06)"
                    : "0px 3.708609104156494px 37.086090087890625px rgba(4.12, 38.84, 18.01, 0.06)",
                }}
              >
                <p className="text-xl font-medium">
                  {isFeatured ? (
                    <>
                      {intervalCount}
                      &nbsp;
                      {t(`pricing.interval.${interval}`)}
                    </>
                  ) : (
                    <GradientText>
                      {intervalCount}
                      &nbsp;
                      {t(`pricing.interval.${interval}`)}
                    </GradientText>
                  )}
                </p>

                <div>
                  <p className="mb-3 text-4xl font-bold">
                    {currencySymbol}
                    {price.metadata?.custom_monthly_unit_amount_to_show &&
                      (
                        Number(
                          price.metadata?.custom_monthly_unit_amount_to_show,
                        ) / 100
                      ).toFixed(2)}

                    <span className="text-xs font-normal">
                      / {t("pricing.frequency.month")}
                    </span>
                  </p>

                  <p
                    className={cn(
                      "text-xs",
                      isFeatured ? "text-white" : "text-neutral-500",
                    )}
                  >
                    {t("price.card.tips")}
                  </p>
                </div>

                <Button
                  id={`upgrade-button-${i18n.language}`}
                  variant={isFeatured ? "secondary" : "default"}
                  className={cn(
                    "w-full min-w-56 rounded-lg",
                    isFeatured
                      ? "border border-[#EEFFEC] hover:bg-neutral-200"
                      : "",
                  )}
                  style={{
                    background: isFeatured
                      ? undefined
                      : "linear-gradient(101deg, #0A4145 -3.56%, #3E986C 36.5%, #A7B542 70.07%, #E1A602 104.71%)",
                  }}
                  onClick={() => {
                    if (!session) {
                      window.location.href = "/login";
                    } else {
                      getCheckoutSession(price.id);
                    }
                  }}
                  disabled={loading}
                >
                  {t("pricing.upgrade.btn")}
                </Button>
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default PricingPlanSection;
